// src/pages/Login.js
import { useState } from "react";
import {
  Box, TextField, Button, Typography, Alert,
  InputAdornment, IconButton, CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, Link as RouterLink, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../api/axios";
import { useAuth } from "../auth/useAuth";

export default function Login() {
  const { t } = useTranslation();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [errMsg,   setErrMsg]   = useState("");

  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    setErrMsg(""); setLoading(true);
    try {
      const form = new URLSearchParams();
      form.append("username", username);
      form.append("password", password);
      const res = await API.post("/auth/login", form, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      const token = res?.data?.access_token;
      if (!token) throw new Error(t("errors.unknown"));
      login(token);
      navigate("/", { replace: true });
    } catch (err) {
      setErrMsg((((d) => typeof d === 'string' ? d : d?.message || d?.detail || JSON.stringify(d))(err?.response?.data?.detail)) || err?.message || t("auth.error_credentials"));
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      bgcolor: "#f8fafc",
      background: "radial-gradient(ellipse 80% 60% at 50% -10%, #eef2ff 0%, #f8fafc 60%)",
    }}>
      <Box sx={{ width: "100%", maxWidth: 400, px: 2 }}>
        {/* Logo / Marque */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2,
            bgcolor: "#6366f1", mx: "auto", mb: 2,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Typography sx={{ color: "white", fontWeight: 800, fontSize: 20 }}>N</Typography>
          </Box>
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
            NextHop Academy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("auth.subtitle_login")}
          </Typography>
        </Box>

        {/* Card */}
        <Box sx={{
          bgcolor: "white", borderRadius: 3,
          border: "1px solid", borderColor: "#e2e8f0",
          p: 3.5, boxShadow: "0 4px 24px rgba(0,0,0,.06)",
        }}
          component="form" onSubmit={handleLogin}>

          <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5 }}>
            {t("auth.title_login")}
          </Typography>

          {errMsg && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{errMsg}</Alert>
          )}

          <TextField
            label={t("auth.username")} fullWidth size="small" margin="normal"
            value={username} onChange={e => setUsername(e.target.value)}
            autoComplete="username" autoFocus
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <TextField
            label={t("auth.password")} type={showPwd ? "text" : "password"}
            fullWidth size="small" margin="normal"
            value={password} onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPwd(v => !v)}>
                    {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          <Button
            type="submit" variant="contained" fullWidth disabled={loading}
            sx={{ mt: 2, py: 1.2, borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, fontWeight: 600 }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: "white" }} /> : t("auth.login")}
          </Button>

          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
            <Typography variant="caption" color="text.secondary">ou</Typography>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
          </Box>

          <Button
            variant="outlined" fullWidth
            onClick={() => window.location.href = "https://ecourses.nexthopacademy.net/connexion?redirect_to=/dashboard"}
            sx={{ mt: 2, py: 1.2, borderRadius: 2, borderColor: "#e2e8f0", color: "#374151", fontWeight: 600, "&:hover": { borderColor: "#6366f1", color: "#6366f1" } }}
          >
            🎓 Se connecter via NextHop Academy
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5, textAlign: "center" }}>
            {t("auth.no_account")}{" "}
            <a href="https://ecourses.nexthopacademy.net/inscription" style={{ color: "#6366f1", fontWeight: 600, textDecoration: "none" }}>
              {t("auth.go_register")}
            </a>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}



